import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FiTrendingUp,
  FiUsers,
  FiCheckCircle,
  FiTruck,
  FiFileText,
  FiCalendar,
  FiPackage,
  FiDollarSign,
  FiAlertTriangle,
  FiChevronRight,
  FiCheck,
  FiClock,
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { modulosVisibles, featureFlagEnabled } from '../config/modulos';
import api from '../utils/api';

const fechaLocalInput = (fecha) => {
  const anio = fecha.getFullYear();
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const dia = String(fecha.getDate()).padStart(2, '0');
  return `${anio}-${mes}-${dia}`;
};

const parsearFechaLocal = (valor) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(valor || '')) return null;
  const [anio, mes, dia] = valor.split('-').map(Number);
  const fecha = new Date(anio, mes - 1, dia);
  return fecha.getFullYear() === anio && fecha.getMonth() === mes - 1 && fecha.getDate() === dia
    ? fecha
    : null;
};

const claveFechaLocal = (fecha) => fechaLocalInput(fecha);

const fechasIniciales = () => {
  const hasta = new Date();
  hasta.setHours(0, 0, 0, 0);
  const desde = new Date(hasta);
  desde.setDate(desde.getDate() - 29);
  return { desde: fechaLocalInput(desde), hasta: fechaLocalInput(hasta) };
};

export default function Dashboard() {
  const { usuario, empresa, empresaActivaId } = useAuth();
  const modulos = modulosVisibles(usuario, empresa).filter((m) => !m.core);

  const esSuperadmin = usuario?.rol === 'superadmin';
  const itvActivo = esSuperadmin || (empresa?.modulos?.citas_itv === true && featureFlagEnabled(empresa, 'itv_adelantador'));
  const itvCreditos = esSuperadmin ? null : (empresa?.itvCreditos ?? 0);

  const [stats, setStats] = useState(null);
  const [facturasLista, setFacturasLista] = useState([]);
  const [fechasGrafica] = useState(fechasIniciales);
  const [fechaDesde, setFechaDesde] = useState(fechasGrafica.desde);
  const [fechaHasta, setFechaHasta] = useState(fechasGrafica.hasta);
  const [proximasCitasLista, setProximasCitasLista] = useState([]);
  const [tareasPendientesLista, setTareasPendientesLista] = useState([]);
  const [stockAlertasLista, setStockAlertasLista] = useState([]);
  const [presupuestosPendientesLista, setPresupuestosPendientesLista] = useState([]);

  const cargarDatosDashboard = () => {
    const params = esSuperadmin && empresaActivaId ? { empresaId: empresaActivaId } : {};
    if (esSuperadmin && !empresaActivaId) return;

    Promise.allSettled([
      api.get('/clientes', { params }),
      api.get('/tareas', { params }),
      api.get('/facturas', { params }),
      api.get('/citas', { params }),
      api.get('/presupuestos', { params }),
      api.get('/almacen/alertas', { params }),
    ]).then(([cli, tar, fac, cit, pre, alm]) => {
      const clientes = cli.status === 'fulfilled' && Array.isArray(cli.value.data) ? cli.value.data : [];
      const tareas = tar.status === 'fulfilled' && Array.isArray(tar.value.data) ? tar.value.data : [];
      const facturas = fac.status === 'fulfilled' && Array.isArray(fac.value.data) ? fac.value.data : [];
      const citas = cit.status === 'fulfilled' && Array.isArray(cit.value.data) ? cit.value.data : [];
      const presupuestos = pre.status === 'fulfilled' && Array.isArray(pre.value.data) ? pre.value.data : [];
      const stockAlertas = alm.status === 'fulfilled' && Array.isArray(alm.value.data) ? alm.value.data : [];
      setFacturasLista(facturas);

      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      const manana = new Date(hoy);
      manana.setDate(manana.getDate() + 1);

      // El KPI sigue contando solo las citas de hoy.
      const citasHoy = citas
        .filter((c) => {
          const d = new Date(c.inicio);
          return d >= hoy && d < manana;
        })
        .sort((a, b) => new Date(a.inicio) - new Date(b.inicio));

      const proximasCitas = citas
        .filter((c) => {
          const inicio = new Date(c.inicio);
          return !Number.isNaN(inicio.getTime()) && inicio >= hoy;
        })
        .sort((a, b) => new Date(a.inicio) - new Date(b.inicio));

      // Facturación total y cobrada / pendiente
      const totalFacturado = facturas
        .filter((f) => f.estado !== 'anulada')
        .reduce((s, f) => s + (f.total || 0), 0);

      const pendienteCobro = facturas
        .filter((f) => f.estado === 'emitida' || f.estado === 'pendiente')
        .reduce((s, f) => s + (f.total || 0), 0);

      // Presupuestos
      const presupuestosAceptados = presupuestos.filter((p) => p.estado === 'aceptado');
      const presupuestosEnviados = presupuestos.filter((p) => p.estado === 'enviado' || p.estado === 'pendiente');
      const montoPresupuestosPendientes = presupuestosEnviados.reduce((s, p) => s + (p.total || 0), 0);

      const tasaConversion = presupuestos.length
        ? Math.round((presupuestosAceptados.length / presupuestos.length) * 100)
        : 0;

      // Tareas pendientes y urgentes
      const tareasPendientes = tareas.filter((t) => t.estado !== 'completado');
      const tareasCompletadas = tareas.filter((t) => t.estado === 'completado').length;
      const tareasUrgentes = tareasPendientes.filter((t) => t.urgencia === 'alta' || t.urgencia === 'critica' || t.diasPreaviso != null);

      setStats({
        clientes: clientes.filter((c) => c.activo !== false).length,
        citasHoy: citasHoy.length,
        totalFacturado,
        pendienteCobro,
        tasaConversion,
        tareasCompletadas,
        tareasPendientes: tareasPendientes.length,
        tareasUrgentes: tareasUrgentes.length,
        presupuestosPendientes: presupuestosEnviados.length,
        montoPresupuestosPendientes,
        stockAlertasCount: stockAlertas.length,
      });

      setProximasCitasLista(proximasCitas.slice(0, 5));
      setTareasPendientesLista(
        tareasPendientes
          .sort((a, b) => (b.urgencia === 'critica' ? 2 : b.urgencia === 'alta' ? 1 : 0) - (a.urgencia === 'critica' ? 2 : a.urgencia === 'alta' ? 1 : 0))
          .slice(0, 5)
      );
      setStockAlertasLista(stockAlertas.slice(0, 4));
      setPresupuestosPendientesLista(presupuestosEnviados.slice(0, 4));
    });
  };

  useEffect(() => {
    cargarDatosDashboard();
  }, [esSuperadmin, empresaActivaId]);

  const completarTareaRapida = async (tareaId) => {
    try {
      await api.put(`/tareas/${tareaId}`, { estado: 'completado' });
      cargarDatosDashboard();
    } catch (_) {
      alert('No se pudo completar la tarea');
    }
  };

  const KPIS = stats
    ? [
        {
          icono: FiDollarSign,
          label: 'Total facturado',
          valor: stats.totalFacturado.toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }),
          subtext: stats.pendienteCobro > 0 ? `${stats.pendienteCobro.toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })} pendientes de cobro` : 'Cobros al día',
          color: '#22c55e',
        },
        {
          icono: FiUsers,
          label: 'Clientes activos',
          ruta: '/app/clientes',
          valor: stats.clientes,
          subtext: 'Base total de clientes',
          color: '#a855f7',
        },
        {
          icono: FiCalendar,
          label: 'Citas hoy',
          ruta: '/app/citas',
          valor: stats.citasHoy,
          subtext: 'Programadas para la jornada',
          color: '#3b82f6',
        },
        {
          icono: FiCheckCircle,
          label: 'Tareas pendientes',
          ruta: '/app/tareas',
          valor: stats.tareasPendientes,
          subtext: `${stats.tareasUrgentes} urgentes o con preaviso`,
          color: stats.tareasUrgentes > 0 ? '#ef4444' : '#10b981',
        },
        {
          icono: FiTrendingUp,
          label: 'Conversión presupuestos',
          valor: `${stats.tasaConversion}%`,
          subtext: `${stats.montoPresupuestosPendientes.toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })} en espera`,
          color: '#f59e0b',
        },
        {
          icono: FiPackage,
          label: 'Alertas de almacén',
          valor: stats.stockAlertasCount,
          subtext: stats.stockAlertasCount > 0 ? 'Productos bajo stock mínimo' : 'Stock bajo control',
          color: stats.stockAlertasCount > 0 ? '#dc2626' : '#64748b',
        },
      ]
    : [];

  const desdeGrafica = parsearFechaLocal(fechaDesde);
  const hastaGrafica = parsearFechaLocal(fechaHasta);
  const errorRangoGrafica = !fechaDesde || !fechaHasta
    ? 'Selecciona las fechas de inicio y fin.'
    : !desdeGrafica || !hastaGrafica
      ? 'Introduce un rango de fechas válido.'
      : desdeGrafica > hastaGrafica
        ? 'La fecha de inicio debe ser anterior o igual a la fecha de fin.'
        : null;
  const diasGrafica = [];
  if (!errorRangoGrafica) {
    const cursor = new Date(desdeGrafica);
    while (cursor <= hastaGrafica) {
      diasGrafica.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
  }
  const totalesPorFecha = new Map();
  if (!errorRangoGrafica) {
    facturasLista.forEach((factura) => {
      if (factura.estado === 'anulada') return;
      const fechaFactura = new Date(factura.fecha || factura.createdAt);
      const totalFactura = Number(factura.total);
      if (Number.isNaN(fechaFactura.getTime()) || !Number.isFinite(totalFactura)) return;
      const clave = claveFechaLocal(fechaFactura);
      if (clave < fechaDesde || clave > fechaHasta) return;
      totalesPorFecha.set(clave, (totalesPorFecha.get(clave) || 0) + totalFactura);
    });
  }
  const serieFacturacion = diasGrafica.map((fecha) => ({
    fecha,
    total: totalesPorFecha.get(claveFechaLocal(fecha)) || 0,
  }));
  const totalRangoFacturacion = serieFacturacion.reduce((total, dia) => total + dia.total, 0);
  const maximoDiaFacturacion = Math.max(0, ...serieFacturacion.map((dia) => dia.total));
  const puntosGrafica = serieFacturacion.map((dia, indice) => {
    const x = serieFacturacion.length < 2 ? 350 : 12 + (indice / (serieFacturacion.length - 1)) * 676;
    const y = 180 - (maximoDiaFacturacion ? (dia.total / maximoDiaFacturacion) * 150 : 0);
    return `${x},${y}`;
  }).join(' ');

  const saludo = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Buenos días';
    if (h < 20) return 'Buenas tardes';
    return 'Buenas noches';
  };

  return (
    <div className="page">
      <div className="dashboard-head-row">
        <div>
          <h1 className="page-title">Panel de control</h1>
          <p className="page-subtitle">
            {saludo()}{usuario?.nombre ? `, ${usuario.nombre}` : ''}. Resumen operativo de{' '}
            {esSuperadmin ? 'la plataforma' : (empresa?.nombre || 'tu empresa')}.
          </p>
        </div>

      </div>

      {esSuperadmin && !empresaActivaId && (
        <div className="alert-info" style={{ marginBottom: 24 }}>
          Selecciona una empresa en el menú superior para ver las estadísticas.
        </div>
      )}

      {stats && (
        <>
          {/* Fila de Tarjetas KPI */}
          <div className="kpi-grid">
            {KPIS.map((k, i) => {
              const Icono = k.icono;
              return (
                <motion.div
                  key={k.label}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  {k.ruta ? (
                    <Link
                      to={k.ruta}
                      className="kpi-card"
                      aria-label={`${k.label}: ${k.valor}`}
                      style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      <div className="kpi-icon" style={{ background: `${k.color}1c`, color: k.color }}>
                        <Icono />
                      </div>
                      <div className="kpi-body">
                        <span className="kpi-value">{k.valor}</span>
                        <span className="kpi-label">{k.label}</span>
                        {k.subtext && <span className="kpi-subtext">{k.subtext}</span>}
                      </div>
                    </Link>
                  ) : (
                    <div className="kpi-card">
                      <div className="kpi-icon" style={{ background: `${k.color}1c`, color: k.color }}>
                        <Icono />
                      </div>
                      <div className="kpi-body">
                        <span className="kpi-value">{k.valor}</span>
                        <span className="kpi-label">{k.label}</span>
                        {k.subtext && <span className="kpi-subtext">{k.subtext}</span>}
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>

          <section className="dashboard-widget" aria-labelledby="facturacion-chart-title" style={{ marginBottom: 24 }}>
            <div className="dashboard-widget-header">
              <h2 id="facturacion-chart-title" className="dashboard-widget-title">
                <FiTrendingUp style={{ color: '#22c55e' }} /> Facturación por fecha
              </h2>
              <strong aria-live="polite">
                {errorRangoGrafica ? '—' : totalRangoFacturacion.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
              </strong>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
              <label style={{ display: 'grid', gap: 6 }}>
                <span>Desde</span>
                <input type="date" value={fechaDesde} onChange={(evento) => setFechaDesde(evento.target.value)} aria-label="Fecha desde" />
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span>Hasta</span>
                <input type="date" value={fechaHasta} onChange={(evento) => setFechaHasta(evento.target.value)} aria-label="Fecha hasta" />
              </label>
            </div>
            {errorRangoGrafica ? (
              <p role="alert" style={{ margin: 0, color: 'var(--text-soft)' }}>{errorRangoGrafica}</p>
            ) : (
              <>
                <svg
                  viewBox="0 0 700 200"
                  role="img"
                  aria-label={`Facturación diaria desde ${fechaDesde} hasta ${fechaHasta}`}
                  style={{ display: 'block', width: '100%', height: 'auto', minHeight: 120, overflow: 'visible' }}
                >
                  <line x1="12" y1="180" x2="688" y2="180" stroke="var(--border-color, #d1d5db)" strokeWidth="1" />
                  <polyline points={puntosGrafica} fill="none" stroke="#22c55e" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
                </svg>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, color: 'var(--text-soft)', fontSize: '0.85rem' }}>
                  <span>{desdeGrafica.toLocaleDateString('es-ES')}</span>
                  <span>{hastaGrafica.toLocaleDateString('es-ES')}</span>
                </div>
                {totalRangoFacturacion === 0 && (
                  <p style={{ margin: '12px 0 0', color: 'var(--text-soft)' }}>No hay facturación en este rango.</p>
                )}
              </>
            )}
          </section>

          {/* MÁXIMA UTILIDAD: WIDGETS OPERATIVOS EN TIEMPO REAL */}
          <div className="dashboard-widgets-grid">
            {/* Widget 1: Próximas citas */}
            <div className="dashboard-widget">
              <div className="dashboard-widget-header">
                <span className="dashboard-widget-title">
                  <FiCalendar style={{ color: '#3b82f6' }} /> Próximas citas ({proximasCitasLista.length})
                </span>
                <Link to="/app/citas" className="dashboard-widget-link">
                  Ver agenda <FiChevronRight />
                </Link>
              </div>
              <div className="dashboard-widget-list">
                {proximasCitasLista.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-soft)' }}>
                    <p style={{ margin: 0, fontSize: '0.9rem' }}>No hay próximas citas agendadas.</p>
                    <Link to="/app/citas" className="btn mini" style={{ marginTop: 10, display: 'inline-flex' }}>
                      + Agendar cita
                    </Link>
                  </div>
                ) : (
                  proximasCitasLista.map((c) => (
                    <div key={c._id} className="dashboard-item-card">
                      <div className="dashboard-item-info">
                        <span className="dashboard-item-title">{c.clienteId?.nombre || c.titulo || 'Cita programada'}</span>
                        <span className="dashboard-item-sub">
                          <FiClock style={{ verticalAlign: '-1px', marginRight: 4 }} />
                          {new Date(c.inicio).toLocaleDateString('es-ES') !== new Date().toLocaleDateString('es-ES')
                            ? `${new Date(c.inicio).toLocaleDateString('es-ES')} · `
                            : ''}
                          {new Date(c.inicio).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                          {c.notas ? ` · ${c.notas}` : ''}
                        </span>
                      </div>
                      <span className="badge badge-info">{c.estado || 'Programada'}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Widget 2: Tareas Pendientes & Urgentes con Check Directo */}
            <div className="dashboard-widget">
              <div className="dashboard-widget-header">
                <span className="dashboard-widget-title">
                  <FiCheckCircle style={{ color: '#10b981' }} /> Tareas Pendientes
                </span>
                <Link to="/app/tareas" className="dashboard-widget-link">
                  Ir a tareas <FiChevronRight />
                </Link>
              </div>
              <div className="dashboard-widget-list">
                {tareasPendientesLista.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-soft)' }}>
                    <p style={{ margin: 0, fontSize: '0.9rem' }}>¡Todo al día! Sin tareas pendientes.</p>
                  </div>
                ) : (
                  tareasPendientesLista.map((t) => (
                    <div key={t._id} className="dashboard-item-card">
                      <div className="dashboard-item-info">
                        <span className="dashboard-item-title">{t.titulo}</span>
                        <span className="dashboard-item-sub">
                          Asignada a: {t.asignadoA?.nombre || 'Sin asignar'}
                          {t.fechaTope ? ` · Vence: ${new Date(t.fechaTope).toLocaleDateString('es-ES')}` : ''}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="dashboard-quick-check"
                        onClick={() => completarTareaRapida(t._id)}
                        title="Marcar como completada al instante"
                      >
                        <FiCheck />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Widget 3: Alertas Operativas (Stock Bajo & Presupuestos por Responder) */}
            <div className="dashboard-widget">
              <div className="dashboard-widget-header">
                <span className="dashboard-widget-title">
                  <FiAlertTriangle style={{ color: '#f59e0b' }} /> Alertas Operativas
                </span>
                <Link to="/app/almacen" className="dashboard-widget-link">
                  Almacén / Presupuestos <FiChevronRight />
                </Link>
              </div>
              <div className="dashboard-widget-list">
                {stockAlertasLista.length === 0 && presupuestosPendientesLista.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-soft)' }}>
                    <p style={{ margin: 0, fontSize: '0.9rem' }}>Sin alertas de stock ni presupuestos atascados.</p>
                  </div>
                ) : (
                  <>
                    {stockAlertasLista.map((p) => (
                      <div key={p._id} className="dashboard-item-card" style={{ borderColor: '#fca5a5' }}>
                        <div className="dashboard-item-info">
                          <span className="dashboard-item-title" style={{ color: '#b91c1c' }}>
                            <FiPackage style={{ verticalAlign: '-1px', marginRight: 4 }} /> {p.nombre}
                          </span>
                          <span className="dashboard-item-sub">
                            Stock actual: <strong>{p.stock}</strong> (Mínimo: {p.stockMinimo})
                          </span>
                        </div>
                        <span className="badge badge-danger">Reordenar</span>
                      </div>
                    ))}

                    {presupuestosPendientesLista.map((p) => (
                      <div key={p._id} className="dashboard-item-card">
                        <div className="dashboard-item-info">
                          <span className="dashboard-item-title">
                            <FiFileText style={{ verticalAlign: '-1px', marginRight: 4 }} /> {p.titulo || `Presupuesto #${p.numero || ''}`}
                          </span>
                          <span className="dashboard-item-sub">
                            Cliente: {p.clienteId?.nombre || 'Cliente'} · {p.total ? p.total.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }) : '—'}
                          </span>
                        </div>
                        <span className="badge badge-warning">Esperando respuesta</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Módulos de la empresa */}
      <h2 className="section-title" style={{ marginTop: 32 }}>Tus módulos contratados</h2>
      <div className="modulo-grid">
        {modulos.length === 0 && (
          <p className="empty">No tienes módulos activos todavía.</p>
        )}
        {modulos.map((m) => {
          const Icono = m.icono;
          return (
            <Link key={m.clave} to={m.ruta} className="modulo-card">
              <Icono />
              <span>{m.nombre}</span>
            </Link>
          );
        })}
      </div>

      {itvActivo && (
        <>
          <h2 className="section-title" style={{ marginTop: 32 }}>Herramientas integradas</h2>
          <Link to="/app/adelantador-itv" className="itv-cta">
            <div className="itv-cta-icon"><FiTruck /></div>
            <div className="itv-cta-body">
              <span className="itv-cta-title">Adelantador de cita ITV</span>
              <span className="itv-cta-sub">Monitoriza itvcita.com y consigue una cita antes de tu fecha límite.</span>
            </div>
            <div className="itv-cta-cred">
              <span className="itv-cta-cred-num">{esSuperadmin ? '∞' : itvCreditos}</span>
              <span className="itv-cta-cred-lbl">créditos</span>
            </div>
          </Link>
        </>
      )}
    </div>
  );
}
